import { Fragment } from 'react';
import { units, getOuterSlicePath, getOuterTextCoordinates } from '../../StudentStrengths/strengthsHelpers';
import { calculateScore } from '../../StrengthsReport/StudentRow';
import { Assignment, Module, EmbeddedContent } from '@peekapak/types/LessonPlanTypes';
import styles from './StrengthsCircleMobile.module.scss';

interface StrengthsQuestions {
  index: number;
  question1: string;
  question2: string;
  strength1: string;
  strength2: string;
}

interface StrengthsExplanation {
  strength: string;
  description: string;
}

export interface StrengthsEvaluation extends EmbeddedContent {
  questions: StrengthsQuestions[];
  tiebreakerQuestions: StrengthsQuestions[];
  content: StrengthsExplanation[];
}

interface Props {
  handleUnitClick: (arg0: string, arg1: Assignment, arg2: string) => void;
  selectedUnit: string;
  assignments: Assignment[] | [];
  data: Module[];
}

const StrengthsCircle = ({ handleUnitClick, selectedUnit, assignments, data }: Props): JSX.Element => {
  const handleClick = (unit: string, assignment: Assignment, strength: string) => {
    handleUnitClick(unit, assignment, strength);
  };

  const getStudentStrength = (assignment: Assignment) => {
    const moduleData =
      data &&
      data?.find((item) => item.id === assignment.module) &&
      (data!
        .find((item) => item.id === assignment.module)!
        .studentContent[0].content.find(
          (item) => item.type === 'strengthsEvaluation',
        ) as unknown as StrengthsEvaluation);
    if (!moduleData) return;
    const strength = calculateScore(assignment, moduleData);
    return strength;
  };

  const getFillColor = (status: string, color: string) => {
    if (status !== 'not assigned') return color;
    return '#a5a5a5';
  };

  const getClassName = (status: string, selectedUnit: string, idStub: string) => {
    if (selectedUnit === idStub) return styles.outerActive;
    if (status !== ('submitted' || 'not assigned')) return styles.incomplete;
    return styles.outerSegment;
  };

  const xOrigin = 175;
  const yOrigin = 175;
  const radius = 40;
  const radiansPerUnit = (2 * Math.PI) / units.length;

  return (
    <div>
      <svg height='350px' width='350px' className={styles.pieArea}>
        <Fragment key='inner ring'>
          <g className={styles.segment}>
            <circle cx={xOrigin} cy={yOrigin} r={radius} fill='#012a73' />
          </g>
          <text
            className={styles.innerText}
            transform={'rotate(90)'}
            x={xOrigin}
            y={-yOrigin - 2}
            fontSize={'12px'}
            fill={'white'}
            textAnchor='middle'
          >
            MY
          </text>
          <text
            className={styles.innerText}
            transform={'rotate(90)'}
            x={xOrigin}
            y={-yOrigin + 10}
            fontSize={'12px'}
            fill={'white'}
            textAnchor='middle'
          >
            STRENGTHS
          </text>
          {units.map((unit, index) => {
            const { idStub, title, color } = unit;
            const thisUnit = assignments.find((assignment) => assignment.unit.includes(idStub)) ?? {
              status: 'not assigned',
            };
            const { status } = thisUnit;
            const emotionPathValue = getOuterSlicePath(
              xOrigin,
              yOrigin,
              index * radiansPerUnit,
              radiansPerUnit,
              radius,
            );
            const [outerTextX, outerTextY] = getOuterTextCoordinates(
              xOrigin,
              yOrigin,
              index * radiansPerUnit,
              radiansPerUnit,
              radius * 2.25,
            );
            const strength = status === 'submitted' ? getStudentStrength(thisUnit) : ['incomplete'];
            return (
              <Fragment key={idStub}>
                <g
                  className={getClassName(status, selectedUnit, idStub)}
                  onClick={() => handleClick(idStub, thisUnit, strength)}
                >
                  <path d={emotionPathValue} fill={getFillColor(status, color)} stroke='black' />
                </g>

                <foreignObject
                  requiredFeatures='http://www.w3.org/TR/SVG11/feature#Extensibility'
                  width='90'
                  height='100'
                  transform={`rotate(${90}, ${outerTextX}, ${outerTextY})`}
                  x={outerTextX + 25}
                  y={outerTextY - 25}
                  style={{ pointerEvents: 'none' }}
                >
                  <div className={styles.textContainer}>
                    {status === 'submitted' && (
                      <>
                        <h3 className={styles.submitted}>{strength}</h3>
                        <p className={styles.unitTitleSubmitted}>{title}</p>
                      </>
                    )}
                    {status !== 'submitted' && (
                      <>
                        <h3
                          className={status === 'not assigned' ? styles.notSubmittedUnclickable : styles.notSubmitted}
                        >
                          {status}
                        </h3>
                        <p className={styles.unitTitle}>{title}</p>
                      </>
                    )}
                  </div>
                </foreignObject>
              </Fragment>
            );
          })}
        </Fragment>
      </svg>
    </div>
  );
};

export default StrengthsCircle;
